import React, { createContext, useContext, useMemo, useState } from 'react';
import type { RAGCitation } from '@/services/api';

export type AskMode = 'ai' | 'staff' | 'community';

interface ChatShellContextValue {
  showSources: boolean;
  setShowSources: (show: boolean) => void;
  citations: RAGCitation[];
  setCitations: (citations: RAGCitation[]) => void;
  askMode: AskMode;
  setAskMode: (mode: AskMode) => void;
  communityVersion: number;
  noteCommunityPost: () => void;
}

const ChatShellContext = createContext<ChatShellContextValue | null>(null);

export function ChatShellProvider({ children }: { children: React.ReactNode }) {
  const [showSources, setShowSources] = useState(false);
  const [citations, setCitations] = useState<RAGCitation[]>([]);
  const [askMode, setAskMode] = useState<AskMode>('ai');
  const [communityVersion, setCommunityVersion] = useState(0);
  const noteCommunityPost = React.useCallback(() => {
    setCommunityVersion((version) => version + 1);
  }, []);

  const value = useMemo(
    () => ({
      showSources,
      setShowSources,
      citations,
      setCitations,
      askMode,
      setAskMode,
      communityVersion,
      noteCommunityPost,
    }),
    [showSources, citations, askMode, communityVersion, noteCommunityPost],
  );

  return <ChatShellContext.Provider value={value}>{children}</ChatShellContext.Provider>;
}

export function useChatShell() {
  const ctx = useContext(ChatShellContext);
  if (!ctx) {
    throw new Error('useChatShell must be used within ChatShellProvider');
  }
  return ctx;
}
